// ---------------------------------------------------------------------------
// AppGraphPage — /studio/map route component.
//
// OpenTology-inspired knowledge-graph viewer for the currently loaded app.
// Layout: full-width canvas + fixed right-hand side panel stack
// (Stats → Legend → Node Detail → Sessions).
//
// Phase 1 keeps the ETL in-memory (pulled from `studio-store.currentProject`)
// so there's no KG round-trip on the hot path. Phase 2+ can adopt
// `@cogo/kg-repository` for cross-app and persistent queries.
// ---------------------------------------------------------------------------

import { useMemo, useState } from 'react'
import { Boxes, FileInput, FolderOpen, Network } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import type { StudioRoute } from '@/store/types'
import { DashboardContainer } from '@/features/studio-dashboard/components/dashboard-container'
import { PageHeader } from '@/components/page-header'
import { pushStudioPath, pushStudioRoute } from '@/lib/studio-navigation'
import { useStudioLocale } from '@/i18n'
import { projectToGraph } from './etl/project-to-graph'
import { GraphCanvas } from './graph-canvas'
import { StatsPanel } from './panels/stats-panel'
import { LegendPanel } from './panels/legend-panel'
import { NodeDetailPanel } from './panels/node-detail-panel'
import { SessionsPanel } from './panels/sessions-panel'
import type { AppGraphNodeType } from './types'

export function AppGraphPage() {
  const { strings } = useStudioLocale()
  const currentProject = useStudioStore((s) => s.currentProject)
  const projectMeta = useStudioStore((s) => s.projectMeta)
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute)

  const graph = useMemo(() => projectToGraph(currentProject), [currentProject])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<AppGraphNodeType | null>(null)

  const mapCopy = strings.appMap
  const appName =
    projectMeta?.name ??
    (currentProject?.manifest?.name as string | undefined) ??
    mapCopy.noAppLoaded

  function navigateTo(route: StudioRoute) {
    setStudioRoute(route)
    if (route === 'new-project') {
      pushStudioPath('/studio/new')
      return
    }
    pushStudioRoute(route)
  }

  if (!currentProject) {
    return (
      <DashboardContainer className="p-6 space-y-6 max-w-7xl">
        <PageHeader
          route="map"
          title={strings.routeHeadings.map}
          description={mapCopy.description}
        />
        <section className="rounded-xl border border-dashed border-border bg-card p-10 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Network className="h-6 w-6" />
          </div>
          <h2 className="mt-4 text-sm font-semibold text-foreground">{mapCopy.emptyTitle}</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-muted-foreground">
            {mapCopy.emptyBody}
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {[
              { label: mapCopy.createApp, route: 'new-project' as const, icon: Boxes },
              { label: mapCopy.importSource, route: 'import' as const, icon: FileInput },
              { label: mapCopy.openProjects, route: 'projects' as const, icon: FolderOpen },
            ].map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => navigateTo(item.route)}
                  className="flex h-10 items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                >
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  {item.label}
                </button>
              )
            })}
          </div>
        </section>
      </DashboardContainer>
    )
  }

  return (
    <DashboardContainer className="flex h-full max-w-7xl flex-col gap-4 p-6">
      <PageHeader
        route="map"
        title={strings.routeHeadings.map}
        description={appName}
        action={
          <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground shadow-sm">
            <span className="font-mono text-foreground">{graph.nodes.length}</span>{' '}
            {mapCopy.nodesUnit} ·{' '}
            <span className="font-mono text-foreground">{graph.edges.length}</span>{' '}
            {mapCopy.edgesUnit}
          </div>
        }
      />

      <section className="flex min-h-[560px] flex-1 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="min-w-0 flex-1 border-r border-border/40">
          <GraphCanvas
            graph={graph}
            selectedId={selectedId}
            onSelect={setSelectedId}
            typeFilter={typeFilter}
          />
        </div>

        <aside
          className="flex w-72 shrink-0 flex-col overflow-y-auto bg-background"
          data-testid="app-graph-panels"
        >
          <StatsPanel graph={graph} />
          <LegendPanel
            activeFilter={typeFilter}
            onFilterChange={setTypeFilter}
          />
          <NodeDetailPanel graph={graph} selectedId={selectedId} />
          <SessionsPanel />
        </aside>
      </section>
    </DashboardContainer>
  )
}
